import React, { useEffect, useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { loadIconSource } from './IconManifest.js';

const LEGACY_SPOT_ICON_BASE_URL =
  'https://pladdypusv2.s3.ap-southeast-2.amazonaws.com/public/spot_icons/';

export default function IconPicker({
  value,
  onChange,
  manifestUrl,
  placeholder = 'Search icons...',
  disabled = false,
  className = '',
}) {
  const [loading, setLoading] = useState(true);
  const [baseUrl, setBaseUrl] = useState('');
  const [icons, setIcons] = useState([]);
  const [q, setQ] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const src = await loadIconSource(manifestUrl);
        if (!alive) return;
        setBaseUrl(src.baseUrl || '');
        setIcons(src.icons || []);
      } catch {
        if (!alive) return;
        setBaseUrl('');
        setIcons([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [manifestUrl]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    const originalOrder = new Map(icons.map((icon, index) => [icon.value, index]));
    const matches = !term
      ? icons
      : icons.filter((icon) =>
        icon.name.toLowerCase().includes(term) ||
        icon.value.toLowerCase().includes(term) ||
        String(icon.url || '').toLowerCase().includes(term)
      );

    return [...matches].sort((left, right) => {
      const leftIsLocal = String(left.url || '').startsWith('/spot_icons/');
      const rightIsLocal = String(right.url || '').startsWith('/spot_icons/');
      if (leftIsLocal !== rightIsLocal) return leftIsLocal ? -1 : 1;
      if (leftIsLocal && rightIsLocal) {
        return (originalOrder.get(left.value) ?? 0) - (originalOrder.get(right.value) ?? 0);
      }
      return left.name.localeCompare(right.name);
    });
  }, [q, icons]);

  const normalizeSpotIconUrl = useCallback((urlLike) => {
    const value = String(urlLike || '').trim();
    if (!value) return '';

    if (value.startsWith('/spot_icons/')) {
      return `${LEGACY_SPOT_ICON_BASE_URL}${value.slice('/spot_icons/'.length)}`;
    }

    const marker = '/public/spot_icons/';
    const markerIndex = value.indexOf(marker);
    if (markerIndex >= 0) {
      const fileName = value.slice(markerIndex + marker.length);
      return `${LEGACY_SPOT_ICON_BASE_URL}${fileName}`;
    }

    return value;
  }, []);

  const handleSelect = useCallback((icon) => {
    if (disabled) return;
    const url = icon.url || `${baseUrl}${icon.value}`;
    onChange?.(url);
  }, [baseUrl, disabled, onChange]);

  const handleClear = useCallback(() => {
    if (disabled) return;
    onChange?.(null);
  }, [disabled, onChange]);

  // For preview: if the stored URL points to a /public/spot_icons/ path (spot icon),
  // display it using the local CMS path so it shows even when the S3 file doesn't exist yet.
  const previewUrl = (() => {
    if (!value) return '';
    if (value.startsWith('/spot_icons/')) return value;
    const marker = '/public/spot_icons/';
    if (value.indexOf(marker) >= 0) return value; // S3 URL, load directly
    return normalizeSpotIconUrl(value);
  })();

  return (
    <div className={`w-full ${className}`}>
      <div className="mt-2 flex items-center gap-3 rounded-xl border border-slate-200 bg-gradient-to-r from-white to-slate-50 px-4 py-3">
        <span className="text-sm font-medium tracking-[0.02em] text-slate-700">Selected Icon:</span>
        {previewUrl ? (
          <span className="flex items-center gap-3">
            <span className="flex h-12 w-12 items-center justify-center rounded-xl border border-slate-200 bg-white shadow-sm">
              <img src={previewUrl} alt="selected icon" className="h-8 w-8 object-contain" />
            </span>
            <a
              href={previewUrl}
              target="_blank"
              rel="noreferrer"
              className="text-sm font-medium text-blue-700 underline underline-offset-2"
            >
              Open
            </a>
          </span>
        ) : null}
      </div>

      <div className="mt-3 flex items-center gap-2">
        <input
          type="text"
          className="block w-full rounded-xl border border-slate-200 px-3 py-2 text-sm shadow-sm"
          placeholder={placeholder}
          value={q}
          onChange={(event) => setQ(event.target.value)}
          disabled={disabled}
          aria-label="Search icons"
        />
        <button
          type="button"
          className="rounded-xl border border-slate-200 px-4 py-2 text-sm text-red-600 disabled:opacity-50"
          onClick={handleClear}
          disabled={disabled || !value}
          title="Clear selection"
        >
          Clear
        </button>
      </div>

      <div className="mt-3 min-h-[120px]">
        {loading ? (
          <div className="text-sm text-gray-500">Loading icons...</div>
        ) : filtered.length === 0 ? (
          <div className="text-sm text-gray-400">No icons</div>
        ) : (
          <div className="grid grid-cols-3 gap-3 max-h-72 overflow-y-auto rounded-2xl border border-slate-200 bg-white p-3 md:grid-cols-5 xl:grid-cols-7">
            {filtered.map((icon) => {
              const url = icon.url || (baseUrl ? `${baseUrl}${icon.value}` : icon.value);
              const resolvedIconUrl = normalizeSpotIconUrl(url);
              const resolvedValue = normalizeSpotIconUrl(value);
              const selected = !!(resolvedValue && resolvedValue === resolvedIconUrl);
              const isLocal = String(icon.url || '').startsWith('/spot_icons/');

              return (
                <button
                  key={icon.value}
                  type="button"
                  onClick={() => handleSelect(icon)}
                  className={`group rounded-2xl border p-3 text-left transition focus:outline-none ${
                    selected
                      ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                      : 'border-slate-200 bg-white hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md'
                  }`}
                  title={icon.name}
                  aria-pressed={selected}
                >
                  <div className="mb-2 flex h-16 items-center justify-center rounded-xl border border-slate-100 bg-slate-50">
                    <img
                      src={url}
                      alt={icon.name}
                      className="h-10 w-10 object-contain transition group-hover:scale-[1.04]"
                    />
                  </div>
                  <div className="truncate text-xs font-medium text-slate-700">{icon.name}</div>
                  <div className="mt-1 text-[10px] uppercase tracking-[0.08em] text-slate-400">
                    {isLocal ? 'Hotel Set' : 'Library'}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

IconPicker.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  manifestUrl: PropTypes.string,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  className: PropTypes.string,
};
